import { call, put, select } from 'redux-saga/effects'
import ChatGroupService from '../../../services/ChatGroup'
import ChatGroupSocket from '../../../services/socket/ChatGroupSocket'
import RESTModelNormalizationSchemas from '../../models/RESTModelNormalizationSchemas'
import createDataStoreSliceFetchHandlers from './helpers/createDataStoreFetchHandlers'
import LayoutActions from '../../actions/Layout'
import ListMetaData from '../../../modules/utilities/list/ListMetaData'
import ErrorActions from '../../actions/Error'
import OLError from '../../../modules/errorHandling/models/OLError'
import { HTTP_RESPONSE_CODES, IS_DEV_ENV } from '../../../constants/general'
import { getDataStoreEntityMap } from '../../../modules/chat/dataStoreEntityMap'

const createStandardHandlers = createDataStoreSliceFetchHandlers(
  'ChatGroup',
  ChatGroupService,
  RESTModelNormalizationSchemas.chatGroup.schema,
  [
    RESTModelNormalizationSchemas.chatGroup.entityName,
    RESTModelNormalizationSchemas.user.entityName,
    RESTModelNormalizationSchemas.device.entityName
  ]
)

function * pruneChatGroupsNotInList (DataStoreSlice, activeIds) {
  const activeIdSet = new Set((activeIds || []).map(id => String(id)))
  const chatGroupsById = getDataStoreEntityMap(yield select(state => state.data.chatGroups))
  for (const id of Object.keys(chatGroupsById)) {
    if (!activeIdSet.has(String(id))) {
      yield put(DataStoreSlice.actions.deleteItem(String(id)))
    }
  }
}

/**
 * Full list uses socket (member count, last activity, unread). REST loads specific ids (edit screen).
 */
const ChatGroupDataStoreSagaHandlers = DataStoreSlice => {
  const standard = createStandardHandlers(DataStoreSlice)

  return {
    ...standard,
    fetch: function * (action) {
      const { data: { ids } } = action
      if (ids && ids.length > 0) {
        yield standard.fetch(action)
        return
      }

      const { fetchId } = standard
      try {
        yield put(LayoutActions.setIsLoading(true, fetchId))
        const result = yield call(
          ChatGroupSocket.FindMany,
          new ListMetaData(ListMetaData.NO_LIMIT, 0, undefined, null)
        )
        yield put(
          DataStoreSlice.actions.fetchResult(
            result,
            function * (storeValues) {
              yield * pruneChatGroupsNotInList(DataStoreSlice, storeValues.result)
            }
          )
        )
      } catch (e) {
        const isUnauthorized = e?.response?.status === HTTP_RESPONSE_CODES.UNAUTHORIZED
        yield put(
          ErrorActions.trigger(
            new OLError(
              '[ChatGroup] DataStoreSagaHandlers.fetch: Failed to fetch chat groups',
              'Failed to fetch chat groups',
              e
            ),
            !isUnauthorized,
            !isUnauthorized,
            !isUnauthorized,
            false
          )
        )
        if (IS_DEV_ENV) {
          console.warn('[ChatGroupDataStore] socket list failed, falling back to REST', e?.message || e)
        }
        yield standard.fetch(action)
        return
      } finally {
        try {
          typeof action.data.onFinish === 'function' && action.data.onFinish()
        } catch (finishErr) {
          if (IS_DEV_ENV) {
            console.error(`[${fetchId}] onFinish error:`, finishErr)
          }
        }
        yield put(LayoutActions.setIsLoading(false, fetchId))
      }
    }
  }
}

export default ChatGroupDataStoreSagaHandlers
